import {
  CallQueueOutSchema,
  MinimalTeamMemberSchema,
  PagedProfileMinimalOutSchemaV2,
  ProfileRole,
} from "@/api/client";
import { handleError } from "@/api/handleError";
import { getCallQueues } from "@/api/requests/callQueue";
import { getTeamMembersV2FromLink } from "@/api/requests/misc";
import { getTeamMembersV2 } from "@/api/requests/teamMembers";
import { useBusinessStore } from "@/stores";
import { useNumberStore } from "@/stores/numbers.store";
import { useTeamStore } from "@/stores/team-member.store";
import type { Ref } from "vue";
import { ref } from "vue";
import { addContactToQueue } from "./queries/callQueue";
import {
  deleteTeamMember,
  exportTeamMembersCSV,
  fetchTeamMembersStatus,
} from "./queries/teamMembers";
import type { AllCallData } from "./types/call";
import { AddContactToQueuePayload } from "./types/callQueue";
import { InviteTeamMemberPayload } from "./types/teams";

export const useTeamMember = {
  teamMemberCallQueue: ref([]) as Ref<any[]>,

  allCallQueues: ref<CallQueueOutSchema[]>([]),

  teamMemberCallRecord: ref([]),

  teamMembersWithStatus: ref() as any,

  callQueueLoading: ref(false),

  teamMemberLoading: ref(false),

  creationDate: ref(""),

  teamMembersStatus: ref() as any,

  callRecordLoading: ref(false),

  extraInfo: ref<Omit<PagedProfileMinimalOutSchemaV2, "data"> | null>(null),

  callList: ref<AllCallData | null>(null),

  listTeamMembers: async (nextUrl?: string | null) => {
    const teamStore = useTeamStore();
    const businessStore = useBusinessStore();

    useTeamMember.teamMemberLoading = ref(true);

    const businessId = businessStore.currentBusinessId;

    let teamMembersList: PagedProfileMinimalOutSchemaV2 | null = null;

    try {
      if (!nextUrl) {
        if (!businessId) {
          throw new Error("Business ID is required");
        }

        const teamMembersResponse = await getTeamMembersV2({
          pageIndex: 1,
          pageSize: 100,
          businessId,
        });

        teamMembersList = teamMembersResponse.data;
      } else {
        const teamMembersResponse = await getTeamMembersV2FromLink({
          link: nextUrl,
        });

        teamMembersList = teamMembersResponse.data;
      }

      const { total, page_size, page_index, nb_pages, previous, next } =
        teamMembersList;

      useTeamMember.extraInfo.value = {
        total,
        page_size,
        page_index,
        nb_pages,
        previous,
        next,
      };

      // @ts-expect-error TODO: Fix this. teamMembersList is minimal while teamMembers is full. So we need to ensure we are not depending on the teamMembers where we need teamMembersList
      teamStore.teamMembers = teamMembersList.data;

      teamStore.teamMembersList = teamMembersList.data;
      useTeamMember.teamMemberLoading = ref(false);
      return teamMembersList.data;
    } catch (error) {
      useTeamMember.teamMemberLoading = ref(false);
      handleError({ error, silent: true });
    }
  },
  teamMembersList: async () => {
    const teamStore = useTeamStore();
    const businessStore = useBusinessStore();

    useTeamMember.teamMemberLoading = ref(true);

    const businessId = businessStore.currentBusinessId;

    try {
      if (!businessId) {
        throw new Error("Business ID is required");
      }

      const teamMemberList = await getTeamMembersV2({
        pageIndex: 1,
        pageSize: 100,
        businessId,
      });

      teamStore.teamMembersList = teamMemberList.data.data;

      return teamMemberList.data.data;
    } catch (error) {
      handleError({ error, silent: true });
    }
  },
  handleDeleteTeamMembers: async (id: number) => {
    try {
      await deleteTeamMember(id);
    } catch (error) {
      handleError({ error });
    }
  },
  handleGetUsersCallQueue: async () => {
    useTeamMember.callQueueLoading.value = true;

    const businessStore = useBusinessStore();

    const businessId = businessStore.currentBusinessId;

    try {
      if (!businessId) {
        throw new Error("Business ID is required");
      }

      const response = await getCallQueues({
        pageSize: 100,
        businessId,
      });

      useTeamMember.allCallQueues.value = response.data.data;
    } catch (error) {
      handleError({ error });
    } finally {
      useTeamMember.callQueueLoading.value = false;
    }
  },

  handleAddContactToQueue: async (
    payload: AddContactToQueuePayload,
    agent_id: number
  ) => {
    try {
      await addContactToQueue(payload, agent_id);
    } catch (error) {
      handleError({ error });
    }
  },
  fetchTeamStatus: async () => {
    // fetch status and map with members
    const teamStore = useTeamStore();

    const numberStore = useNumberStore();
    if (
      // @ts-expect-error TODO: Confirm if the type is in BE
      numberStore.activeNumber?.receiver_role?.toLowerCase() !== "user" &&
      teamStore.teamMembers?.length
    ) {
      // if members exist
      try {
        //  get extensions
        const teamExtensions = teamStore.teamMembers.map((ext: any) =>
          String(ext.extension_code)
        ); // map paginated data cos only 20 extensions can be sent at once
        const teamStatusPayload = {
          extensions: teamExtensions,
        };
        // get the status
        const response = await fetchTeamMembersStatus(teamStatusPayload);

        const teamMembersStatus = response.data;
        // map the status with member array.
        useTeamMember.teamMembersWithStatus.value =
          useTeamMember.updateUsersStatus(
            teamStore.teamMembers,
            teamMembersStatus
          );

        teamStore.teamMembers = useTeamMember.teamMembersWithStatus.value;
      } catch (error) {
        handleError({ error });
      }
    }
  },

  updateUsersStatus: (members: any, extensionStatus: any) => {
    return members.map((entry: any) => {
      const extensionCode = entry.extension_code.toString(); // Convert to string for comparison

      if (extensionStatus.hasOwnProperty(extensionCode)) {
        entry.status = extensionStatus[extensionCode];
      }
      return entry;
    });
  },

  handleTeamMemberExportCSV: async () => {
    try {
      await exportTeamMembersCSV();
    } catch (error) {
      handleError({ error });
    }
  },
  handlePrevious: async () => {
    await useTeamMember.listTeamMembers(
      useTeamMember.extraInfo.value?.previous
    );
  },

  handleNext: async () => {
    await useTeamMember.listTeamMembers(useTeamMember.extraInfo.value?.next);
  },
};

export function getModifiedKeysAndValues(obj1: any, obj2: any) {
  const modifiedKeysAndValues = {} as any;

  for (const key in obj1) {
    if (
      obj1.hasOwnProperty(key) &&
      obj2.hasOwnProperty(key) &&
      obj1[key] !== obj2[key]
    ) {
      modifiedKeysAndValues[key] = obj2[key];
    }
  }

  return modifiedKeysAndValues;
}

// New Team Member Invite flow, Invite multiple team members

export const currentStep = ref(1);

export const memberRoleOptions = ref([
  { title: "Call Agent", value: "agent" },
  { title: "Manager", value: "manager" },
  { title: "Business Owner", value: "owner" },
]);

export const usePhoneNumber = ref(false);

export function generateInvitationPayload(
  data: InviteTeamMemberPayload
): MinimalTeamMemberSchema[] {
  return data.emailList.map((email: string) => ({
    email: email,
    role: data.role as ProfileRole,
    permissions: {
      ...data.permissions,
      business_numbers: data.selectedNumbers,
      role: data.role as ProfileRole,
    },
    can_make_calls: true, // Assuming all emails can make calls
    country: "NG", // Assuming "NG" as the default country
  }));
}
